// Shared CSV + PDF export helpers used by dashboard tables.
// Columns: array of { key: string, label: string, format?: (row) => string|number }
// Rows: array of plain objects.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function safeFilenamePart(s) {
  return (s || "export").replace(/[^a-z0-9-]+/gi, "_").toLowerCase();
}

function stamp() {
  return new Date().toISOString().slice(0, 10);
}

function resolveCell(row, col, i) {
  if (typeof col.format === "function") return col.format(row, i);
  const v = row?.[col.key];
  return v === null || v === undefined ? "" : v;
}

/**
 * Trigger a client-side CSV download.
 * @param {string} filenameBase — base name without extension (will be slugified + date-stamped)
 * @param {Array<{key:string,label:string,format?:Function}>} columns
 * @param {Array<object>} rows
 */
export function downloadCSV(filenameBase, columns, rows) {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const lines = [header];
  rows.forEach((r, i) => {
    lines.push(columns.map((c) => csvEscape(resolveCell(r, c, i))).join(","));
  });
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeFilenamePart(filenameBase)}-${stamp()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Trigger a client-side PDF download rendered by jsPDF + autoTable.
 * @param {string} filenameBase
 * @param {{title:string, subtitle?:string, orientation?:"portrait"|"landscape"}} meta
 * @param {Array<{key:string,label:string,format?:Function,align?:"left"|"right"|"center"}>} columns
 * @param {Array<object>} rows
 */
export function downloadPDF(filenameBase, meta, columns, rows) {
  const orientation = meta.orientation || (columns.length > 6 ? "landscape" : "portrait");
  const doc = new jsPDF({ orientation, unit: "pt", format: "letter" });

  // Editorial-style header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(meta.title || "Black Market Toronto", 40, 44);
  if (meta.subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(meta.subtitle, 40, 60);
  }
  doc.setTextColor(0);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Exported ${new Date().toLocaleString()}`, 40, 74);

  const head = [columns.map((c) => c.label)];
  const body = rows.map((r, i) => columns.map((c) => {
    const v = resolveCell(r, c, i);
    return v === null || v === undefined ? "" : String(v);
  }));

  autoTable(doc, {
    startY: 90,
    head,
    body,
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 4,
      lineColor: [10, 10, 10],
      lineWidth: 0.25,
    },
    headStyles: {
      fillColor: [10, 10, 10],
      textColor: [245, 242, 235],
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: { fillColor: [237, 232, 220] },
    columnStyles: columns.reduce((acc, c, idx) => {
      if (c.align) acc[idx] = { halign: c.align };
      return acc;
    }, {}),
    margin: { left: 40, right: 40 },
  });

  doc.save(`${safeFilenamePart(filenameBase)}-${stamp()}.pdf`);
}

/**
 * PDF export for a single analyst answer: question header, narrative text,
 * optional chart image, and the underlying data table.
 * @param {string} filenameBase
 * @param {{title:string, narrative?:string, chartPng?:string, chartW?:number, chartH?:number}} meta
 * @param {Array<{key:string,label:string}>} columns
 * @param {Array<object>} rows
 */
export function downloadAnswerPDF(filenameBase, meta, columns, rows) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const left = 40;
  const usableW = pageW - left * 2;
  let y = 46;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("BLACK MARKET · ASK THE DATA", left, y);
  y += 18;

  doc.setFontSize(15);
  doc.setTextColor(10);
  const titleLines = doc.splitTextToSize(meta.title || "Analyst answer", usableW);
  doc.text(titleLines, left, y);
  y += titleLines.length * 18 + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`Exported ${new Date().toLocaleString()}`, left, y);
  y += 16;
  doc.setTextColor(10);

  if (meta.narrative) {
    const plain = meta.narrative
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/^\s*[-*]\s+/gm, "• ");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(plain, usableW);
    doc.text(lines, left, y);
    y += lines.length * 13 + 10;
  }

  if (meta.chartPng && meta.chartW && meta.chartH) {
    const imgW = Math.min(usableW, 460);
    const imgH = imgW * (meta.chartH / meta.chartW);
    doc.addImage(meta.chartPng, "PNG", left, y, imgW, imgH);
    y += imgH + 14;
  }

  if (columns && columns.length && rows && rows.length) {
    const head = [columns.map((c) => c.label)];
    const body = rows.map((r) => columns.map((c) => {
      const v = r?.[c.key];
      return v === null || v === undefined ? "" : String(v);
    }));
    autoTable(doc, {
      startY: y,
      head,
      body,
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4, lineColor: [10, 10, 10], lineWidth: 0.25 },
      headStyles: { fillColor: [10, 10, 10], textColor: [245, 242, 235], fontStyle: "bold", halign: "left" },
      alternateRowStyles: { fillColor: [237, 232, 220] },
      margin: { left, right: left },
    });
  }

  doc.save(`${safeFilenamePart(filenameBase)}-${stamp()}.pdf`);
}

/**
 * Small helper for numeric formatting inside export columns.
 */
export const fmt = {
  int: (v) => (v === null || v === undefined ? "" : Number(v).toLocaleString()),
  money0: (v) => (v === null || v === undefined ? "" : `$${Math.round(Number(v)).toLocaleString()}`),
  money2: (v) => (v === null || v === undefined ? "" : `$${Number(v).toFixed(2)}`),
  pct1: (v) => (v === null || v === undefined ? "" : `${Number(v).toFixed(1)}%`),
};
